import { ChangeRequestStatus } from '@account/meta/benefits-enrollment-meta';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApprovalFlowStep } from '@app/components/approval-status-flow/approval-status-flow.component';
import { ApprovalFlowStatus } from '@app/enums';
import { AppDatePipe } from '@app/pipes';
import { AuthService, NotifyService } from '@app/services';
import { Translatable } from '@app/types/translatable.type';
import { IndexView } from '@common/views/index/index.view';
import { BenefitChangeRequest } from '@models/benefits/benefit-change-request.model';
import { BenefitPlan } from '@models/benefits/benefit-plan.model';
import { EmployeeDocument } from '@models/documents/employee-document.model';

@Component({
    templateUrl: './change-request-show.template.html',
    styleUrls: ['./change-request-show.style.scss'],
})
export class ChangeRequestShowView extends IndexView implements OnInit {
    isLoading = false;
    benefitPlan: BenefitPlan;
    changeRequest: BenefitChangeRequest;

    approvalFlowSteps: ApprovalFlowStep[] = [];

    employeeDocument: EmployeeDocument | null;

    headerTitle: Translatable;

    private appDate = new AppDatePipe();

    constructor(
        private route: ActivatedRoute,
        protected auth: AuthService,
        protected notify: NotifyService,
        private router: Router
    ) {
        super();
        this.benefitPlan = this.route.snapshot.data.models.get('benefitPlan');
        this.changeRequest = this.route.snapshot.data.models.get('benefitChangeRequest');
        this.headerTitle = {
            key: 'benefits.views.changeRequest.show.changeRequestDetails',
            params: { name: this.changeRequest.employee.fullName },
        };
        this.approvalFlowSteps = this.getApprovalFlow();
    }

    ngOnInit(): void {
        // If this is a simplified change request using PDF document, we set to be shown in view.
        if (this.changeRequest?.employeeDocument) {
            this.employeeDocument = this.changeRequest.employeeDocument;
            return;
        }
    }

    onApprove(): void {
        this.isLoading = true;
        this.changeRequest
            .approve()
            .then(() => {
                this.notify.success('benefits.views.changeRequest.show.changeRequestApproved');
                this.router.navigate(['/benefits', 'plans', String(this.benefitPlan.id), 'change-requests']);
                this.isLoading = false;
            })
            .catch(() => {
                this.notify.error('benefits.views.changeRequest.show.couldNotSaveChangeRequest');
                this.isLoading = false;
            });
    }

    onDecline(): void {
        this.isLoading = true;
        this.changeRequest
            .decline()
            .then(() => {
                this.notify.success('benefits.views.changeRequest.show.changeRequestDenied');
                this.router.navigate(['/benefits', 'plans', String(this.benefitPlan.id), 'change-requests']);
                this.isLoading = false;
            })
            .catch(() => {
                this.notify.error('benefits.views.changeRequest.show.couldNotSaveChangeRequest');
                this.isLoading = false;
            });
    }

    private getApprovalFlow(): ApprovalFlowStep[] {
        const steps = [
            {
                title: 'benefits.views.changeRequest.show.requestCreated',
                description: this.getRequestCreatedStepDescription(),
                status: ApprovalFlowStatus.PositiveStatus,
                hideIndicator: false,
            },
            {
                title: this.getApprovalFlowStepTitle(),
                description: this.getApprovalFlowStepDescription(),
                status: this.getApprovalFlowStepStatus(),
                hideIndicator: false,
            },
        ];

        return steps;
    }

    private getRequestCreatedStepDescription(): Translatable {
        return {
            key: 'benefits.views.changeRequest.show.requestCreatedMetaDataString',
            params: {
                actorName: this.changeRequest.employee.fullName,
                dateString: this.appDate.transform(this.changeRequest.createdAt, 'MMMM d, y') ?? '-',
            },
        };
    }

    private getApprovalFlowStepDescription(): Translatable {
        let translatableKey = 'benefits.views.changeRequest.show.awaitingApproval';
        if (this.changeRequest.isPending()) {
            return translatableKey;
        }

        if (this.changeRequest.isAccepted()) {
            translatableKey = 'benefits.views.changeRequest.show.requestAccepted';
        }

        if (this.changeRequest.isDeclined()) {
            translatableKey = 'benefits.views.changeRequest.show.requestDeclined';
        }

        return {
            key: translatableKey,
            params: {
                actorName: this.changeRequest.admin.fullName,
                dateString: this.appDate.transform(this.changeRequest.actionedAt, 'MMMM d, y') ?? '-',
            },
        };
    }

    private getApprovalFlowStepStatus(): ApprovalFlowStatus | undefined {
        if (!this.changeRequest.status || this.changeRequest.isPending()) {
            return;
        }

        return this.changeRequest.status === ChangeRequestStatus.accepted
            ? ApprovalFlowStatus.PositiveStatus
            : ApprovalFlowStatus.NegativeStatus;
    }

    private getApprovalFlowStepTitle(): Translatable {
        if (this.changeRequest.isPending()) {
            return 'benefits.views.changeRequest.show.awaitingApproval';
        }

        return this.changeRequest.status === ChangeRequestStatus.accepted
            ? 'benefits.views.changeRequest.show.approved'
            : 'benefits.views.changeRequest.show.denied';
    }
}
